import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { apiRequest } from "services/api";
import { OrganizationModel } from "modules/organization/model";
import { OrganizationUnitModel } from "modules/organization-unit/model";
import { useProductListStore } from "modules/product-list/store";

export interface AppStateModel {
  showFilter: boolean;
  filters: any;
  loading: boolean;
  error: any;
  currentLocation: string | null;
  selectedAttributeFilters: string[];
  selectedOrganizationUuid: string | null;
  selectedOrganizationUnitUuid: string | null;
  selectedOrganizationData: OrganizationModel | null;
  selectedOrganizationUnitData: OrganizationUnitModel | null;
  systemSettings: any;
}

interface AppStore extends AppStateModel {
  clear: () => void;
  selectOrganization: (organizationUuid: string) => Promise<void>;
  loadSystemSettings: () => Promise<void>;
  selectOrganizationUnit: (organizationUnitUuid: string) => Promise<void>;
  refreshOrganizationUnitData: (organizationUnitUuid: string) => Promise<void>;
  refreshOrganizationData: (organizationUuid: string) => Promise<void>;
  addAttributeFilter: (uuid: string) => void;
  clearAttributeFilters: () => void;
  constructFilter: (payload: any) => void;
  toggleFilterVisibility: () => void;
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      showFilter: false,
      filters: {},
      loading: false,
      error: null,
      currentLocation: null,
      selectedAttributeFilters: [],
      selectedOrganizationUuid: null,
      selectedOrganizationUnitUuid: null,
      selectedOrganizationData: null,
      selectedOrganizationUnitData: null,
      systemSettings: null,

      clear: () =>
        set({
          selectedAttributeFilters: [],
          selectedOrganizationUuid: null,
          selectedOrganizationData: null,
        }),

      selectOrganization: async (organizationUuid) => {
        set({ selectedOrganizationUuid: organizationUuid });
        await get().loadSystemSettings();
        try {
          const data = await apiRequest({
            endpoint: `/organizations/${organizationUuid}`,
            method: "get",
          });
          set({ selectedOrganizationData: data });
        } catch {
          // error silently
        }
      },

      loadSystemSettings: async () => {
        try {
          const data = await apiRequest({
            endpoint: "/system-settings/",
            method: "get",
          });
          const settings = Object.fromEntries(
            data.map((item: any) => [item.name, item])
          );
          set({ systemSettings: settings });
        } catch {
          // error silently
        }
      },

      selectOrganizationUnit: async (organizationUnitUuid) => {
        try {
          const data = await apiRequest({
            endpoint: `/organization-units/${organizationUnitUuid}`,
            method: "get",
          });
          set({
            selectedOrganizationUnitUuid: organizationUnitUuid,
            selectedOrganizationUnitData: data,
          });
          useProductListStore.getState().productListLoad(organizationUnitUuid);
        } catch {
          // error silently
        }
      },

      refreshOrganizationUnitData: async (organizationUnitUuid) => {
        try {
          const data = await apiRequest({
            endpoint: `/organization-units/${organizationUnitUuid}`,
            method: "get",
          });
          set({ selectedOrganizationUnitData: data });
        } catch {
          // error silently
        }
      },

      refreshOrganizationData: async (organizationUuid) => {
        try {
          const data = await apiRequest({
            endpoint: `/organizations/${organizationUuid}`,
            method: "get",
          });
          set({ selectedOrganizationData: data });
        } catch {
          // error silently
        }
      },

      addAttributeFilter: (uuid) =>
        set((state) => ({
          selectedAttributeFilters: state.selectedAttributeFilters.includes(uuid)
            ? state.selectedAttributeFilters.filter((x) => x !== uuid)
            : [...state.selectedAttributeFilters, uuid],
        })),

      clearAttributeFilters: () => set({ selectedAttributeFilters: [] }),

      constructFilter: (payload) =>
        set((state) => ({ filters: { ...state.filters, ...payload } })),

      toggleFilterVisibility: () =>
        set((state) => ({ showFilter: !state.showFilter })),
    }),
    {
      name: "app-store",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        selectedOrganizationUuid: state.selectedOrganizationUuid,
        selectedOrganizationUnitUuid: state.selectedOrganizationUnitUuid,
        selectedOrganizationData: state.selectedOrganizationData,
        selectedOrganizationUnitData: state.selectedOrganizationUnitData,
        systemSettings: state.systemSettings,
      }),
    }
  )
);
